import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { ADMIN } from "@/constants/testIds";
import { Eye, EyeOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const getImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${BACKEND_URL}/api/files/${path}`;
};

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(d => d.msg || d).join(" "));
      else setError("Erro ao iniciar sessão. Verifique as credenciais.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111827] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo area */}
        <div className="text-center mb-10">
          <span className="font-dancing text-[#0077B6] text-3xl block">O Quinto Império</span>
          <p className="font-lato text-xs text-gray-500 tracking-widest uppercase mt-1">
            — Painel de Administração —
          </p>
        </div>

        {/* Card */}
        <div className="bg-white p-8 md:p-10 shadow-2xl">
          <h1 className="font-cormorant text-3xl text-[#4A4A4A] mb-6 font-light">
            Entrar no Painel
          </h1>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm font-lato mb-5 rounded">
              {error}
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            data-testid={ADMIN.loginForm}
            className="space-y-5"
          >
            <div>
              <label className="font-lato text-xs tracking-widest uppercase text-[#4A4A4A] block mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                data-testid={ADMIN.emailInput}
                required
                autoComplete="email"
                className="w-full border border-gray-200 px-4 py-3 font-lato text-sm text-[#4A4A4A] focus:outline-none focus:border-[#0077B6] transition-colors"
                placeholder="admin@exemplo.com"
              />
            </div>

            <div>
              <label className="font-lato text-xs tracking-widest uppercase text-[#4A4A4A] block mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  data-testid={ADMIN.passwordInput}
                  required
                  autoComplete="current-password"
                  className="w-full border border-gray-200 px-4 py-3 pr-12 font-lato text-sm text-[#4A4A4A] focus:outline-none focus:border-[#0077B6] transition-colors"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#4A4A4A]"
                  aria-label="Toggle password visibility"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid={ADMIN.loginBtn}
              className="w-full bg-[#0077B6] text-white font-lato text-sm tracking-widest uppercase py-3.5 hover:bg-[#005f8f] transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "A entrar..." : "Entrar"}
            </button>
          </form>
        </div>

        <p className="text-center mt-6 font-lato text-xs text-gray-600">
          <a href="/" className="hover:text-gray-400 transition-colors">
            ← Voltar ao website
          </a>
        </p>
      </div>
    </div>
  );
}
